import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService.js';
import { ReactComponent as Logo } from '../icons/logo_letter.svg';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { analyticsEvents } from '../config/analyticsConfig.js';
import toast from 'react-hot-toast';
import './ForgotPassword.css';

function ForgotPassword() {
  usePageTitle('Recuperar Senha');
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: código, 3: nova senha
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Etapa 1: Solicitar código de recuperação
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor, insira um email válido');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.requestPasswordReset(email);

      if (result.success) {
        analyticsEvents.forgotPassword();
        toast.success('Código enviado para seu email!');
        setStep(2);
      }
    } catch (err) {
      setError(err.message || 'Erro ao solicitar código de recuperação');
    } finally {
      setIsLoading(false);
    }
  };

  // Etapa 2: Validar código
  const handleValidateCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!code || code.trim().length < 4) {
      setError('Por favor, insira o código recebido');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.validateResetCode(email, code);

      if (result.success) {
        toast.success('Código validado com sucesso!');
        setStep(3);
      }
    } catch (err) {
      setError(err.message || 'Código inválido ou expirado');
    } finally {
      setIsLoading(false);
    }
  };

  // Etapa 3: Atualizar senha
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.updatePassword(email, code, newPassword);

      if (result.success) {
        analyticsEvents.resetPassword();
        toast.success('Senha atualizada com sucesso!');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Erro ao atualizar senha');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    if (step === 1) {
      navigate('/');
    } else if (step === 2) {
      setStep(1);
      setCode('');
    } else if (step === 3) {
      setStep(2);
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="step-indicator">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Email</div>
        </div>
        <div className="step-line"></div>
        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Código</div>
        </div>
        <div className="step-line"></div>
        <div className={`step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Nova Senha</div>
        </div>
      </div>
    );
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-background"></div>
      <div className="forgot-password-card">
        <button 
          type="button" 
          className="back-button" 
          onClick={handleBack}
          disabled={isLoading}
          aria-label="Voltar"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <div className="forgot-password-header">
          <div className="logo-wrapper">
            <Logo className="logo-svg" height='70px' />
          </div>
          <h2 className="forgot-password-title">Recuperar Senha</h2>
          <p className="forgot-password-subtitle">
            {step === 1 && 'Digite seu email para receber o código de recuperação.'}
            {step === 2 && 'Digite o código enviado para seu email.'}
            {step === 3 && 'Digite sua nova senha.'}
          </p>
        </div>

        {renderStepIndicator()}

        {/* Etapa 1: Solicitar código */}
        {step === 1 && (
          <form onSubmit={handleRequestCode} className="forgot-password-form">
            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Digite seu email"
                disabled={isLoading}
                autoComplete="email"
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : (
                'Enviar Código'
              )}
            </button>
          </form>
        )}

        {/* Etapa 2: Validar código */}
        {step === 2 && (
          <form onSubmit={handleValidateCode} className="forgot-password-form">
            <div className="input-group">
              <label htmlFor="code">Código de Recuperação</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Digite o código"
                disabled={isLoading}
                autoComplete="off"
                autoFocus
                maxLength={6}
              />
              <p className="input-hint">Verifique seu email e digite o código recebido.</p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : (
                'Validar Código'
              )}
            </button>

            <button 
              type="button" 
              className="resend-button" 
              onClick={() => {
                setStep(1);
                setCode('');
                setError('');
              }}
              disabled={isLoading}
            >
              Reenviar código
            </button>
          </form>
        )}

        {/* Etapa 3: Nova senha */}
        {step === 3 && (
          <form onSubmit={handleUpdatePassword} className="forgot-password-form">
            <div className="input-group">
              <label htmlFor="newPassword">Nova Senha</label>
              <div className="password-input-wrapper">
                <input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite a nova senha"
                  disabled={isLoading}
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
              <div className="password-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Digite a senha novamente"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                  aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              <p className="input-hint">A senha deve ter pelo menos 6 caracteres.</p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : (
                'Atualizar Senha'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
